import { FC, Fragment, JSXElementConstructor, lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import NotFoundComponent from "../components/layout/errors/not-found";
import {
  AVATARS_POPUP_GALLERY,
  MESSAGES_POPUP_GALLERY,
  POPUP_ACTIVATED_GIFT_LIST,
  POPUP_ADD_MY_BILLS,
  POPUP_ADD_NEW_COMMENT,
  POPUP_ADD_TO_HS,
  POPUP_ATTACHMENT_CONTACT,
  POPUP_BILL_INQUIRY,
  POPUP_BRANCH_INFO,
  POPUP_BUY_CHARGE,
  POPUP_BUY_INTERNET_PACKAGE_LIST,
  POPUP_BUY_INTERNET_PACKAGES,
  POPUP_CAMERA,
  POPUP_CARD_INFO,
  POPUP_CONTACT_IMPORT,
  POPUP_CONTACTS,
  POPUP_CONVERT_TO_GROUP,
  POPUP_CREATE_CONVERSATION,
  POPUP_CREATE_GROUP_CHANNEL,
  POPUP_DELETE_ACCOUNT,
  POPUP_EDIT_CONTACT,
  POPUP_EDIT_MY_BILLS,
  POPUP_ELECTRIC_BILL,
  POPUP_FINANCIAL_HISTORY,
  POPUP_FINANCIAL_INFO,
  POPUP_GIFT_CARD_DETAIL,
  POPUP_IGASHT_BUY_TICKET,
  POPUP_IGASHT_LOCATION_SEARCH,
  POPUP_IGASHT_TICKET,
  POPUP_IGASHT_TICKET_LIST,
  POPUP_IGASHT_TICKET_QRCODE,
  POPUP_INQUIRY_MCI,
  POPUP_INQUIRY_TELECOM,
  POPUP_IVAND_HISTORY,
  POPUP_MAIN_VIRTUAL_GIFT,
  POPUP_MAP,
  POPUP_MY_BILLS,
  POPUP_MY_GIFT_LIST,
  POPUP_NEAR_BY,
  POPUP_PROFILE_IVAND,
  POPUP_RECEIPT,
  POPUP_ROOM,
  POPUP_ROOM_ADD_MEMBER,
  POPUP_ROOM_ADMIN_PERMISSION,
  POPUP_ROOM_CHANNEL_EDIT,
  POPUP_ROOM_CHANNEL_TYPE,
  POPUP_ROOM_FORWARD,
  POPUP_ROOM_MEMBER_LIST,
  POPUP_ROOM_PERMISSION,
  POPUP_ROOM_REPORT,
  POPUP_SETTING,
  POPUP_SETTING_CHAT_SETTING,
  POPUP_SETTING_NOTIFICATION,
  POPUP_SETTING_PRIVACY,
  POPUP_SETTING_PRIVACY_BLOCKED,
  POPUP_SETTING_PRIVACY_SESSIONS,
  POPUP_SETTING_PRIVACY_VERIFICATION,
  POPUP_SETTING_PRIVACY_VERIFICATION_SET,
  POPUP_SETTING_PROFILE,
  POPUP_SETTING_PROFILE_PHONE,
  POPUP_SETTING_PROFILE_PHONE_VERIFY,
  POPUP_SETTING_PROFILE_USERNAME,
  POPUP_SETTING_STORAGE,
  POPUP_SHARED_MEDIA,
  POPUP_SHOW_MESSAGE,
  POPUP_STICKER_ADD,
  POPUP_STICKER_GROUP,
  POPUP_STORY_GALLERY,
  POPUP_STORY_VIEWS,
  POPUP_UPDATE,
  POPUP_USERNAME,
  POPUP_VIRTUAL_GIFT_LIST,
  POPUP_WALLET_PEC,
  POPUP_WALLET_PEC_PAYMENT,
  ROUTE_LOGIN,
  ROUTE_LOGIN_CODE,
  ROUTE_LOGIN_NICKNAME,
  ROUTE_LOGIN_PASSWORD,
  ROUTE_LOGIN_RECOVERY_EMAIL,
  ROUTE_LOGIN_RECOVERY_QUESTION,
  ROUTE_MESSENGER,
  ROUTE_ROADMAP,
  ROUTE_TEST,
  ROUTE_TOO_MANY_SESSION,
} from "../constants/routes";
import PrivateRoute from "../components/private-route";
import asyncComponentLoader from "./asyncComponentLoader";
import { TestProto } from "../containers/test-proto";
import { ActiveTab } from "../components/layout/errors/active-tab";
import { checkAuth } from "../libs/auth";
import { getUrlParam } from "../libs/url";
import navigationService from "../libs/navigation";
import { checkHashbang } from "../libs/utils";
import PopupDeleteAccount from "../containers/popup/popup-delete-account";
import PopupSettingProfilePhoneComponent from "../components/popup/setting/profile/phone/phone";

const Login = lazy(() => import("../containers/login"));
const LoginCode = lazy(() => import("../containers/login-code"));
const LoginNickname = lazy(() => import("../containers/login-nickname"));
const LoginPassword = lazy(() => import("../containers/login-password"));
const LoginRecoveryEmail = lazy(() => import("../containers/login-recovery-email"));
const LoginRecoveryQuestion = lazy(() => import("../containers/login-recovery-question"));
const Messenger = lazy(() => import("../containers/messenger"));

const PopupAttachmentContact = asyncComponentLoader(() => import("../containers/popup/popup-attachment-contact").then((module) => module.default));
const PopupAvatarsGallery = asyncComponentLoader(() => import("../containers/popup/popup-avatars-gallery").then((module) => module.default));
const PopupContactImport = asyncComponentLoader(() => import("../containers/popup/popup-contact-import").then((module) => module.default));
const PopupCreateConversation = asyncComponentLoader(() => import("../containers/popup/popup-create-conversation").then((module) => module.default));
const PopupMessagesGallery = asyncComponentLoader(() => import("../containers/popup/popup-messages-gallery").then((module) => module.default));
const PopupProfileIvand = asyncComponentLoader(() => import("../containers/popup/popup-profile-ivand").then((module) => module.default));
const PopupRoomReport = asyncComponentLoader(() => import("../containers/popup/popup-report").then((module) => module.default));
const PopupRoom = asyncComponentLoader(() => import("../containers/popup/popup-room").then((module) => module.default));
const PopupRoomAddMember = asyncComponentLoader(() => import("../containers/popup/popup-room-add-member").then((module) => module.default));
const PopupRoomAdminPermission = asyncComponentLoader(() => import("../containers/popup/popup-room-admin-permission").then((module) => module.default));
const PopupRoomChannelType = asyncComponentLoader(() => import("../containers/popup/popup-room-channel-type").then((module) => module.default));
const PopupRoomChannelEdit = asyncComponentLoader(() => import("../containers/popup/popup-room-chennel-edit").then((module) => module.default));
const PopupRoomCreateGroupChannel = asyncComponentLoader(() => import("../containers/popup/popup-room-create-group-channel").then((module) => module.default));
const PopupRoomForward = asyncComponentLoader(() => import("../containers/popup/popup-room-forward").then((module) => module.default));
const PopupRoomMemberList = asyncComponentLoader(() => import("../containers/popup/popup-room-member-list").then((module) => module.default));
const PopupRoomPermission = asyncComponentLoader(() => import("../containers/popup/popup-room-premission").then((module) => module.default));
const PopupSetting = asyncComponentLoader(() => import("../containers/popup/popup-setting").then((module) => module.default));
const PopupSettingChatSetting = asyncComponentLoader(() => import("../containers/popup/popup-setting-chat-setting").then((module) => module.default));
const PopupSettingNotification = asyncComponentLoader(() => import("../containers/popup/popup-setting-notification").then((module) => module.default));
const PopupSettingPrivacy = asyncComponentLoader(() => import("../containers/popup/popup-setting-privacy").then((module) => module.default));
const PopupSettingPrivacyBlocked = asyncComponentLoader(() => import("../containers/popup/popup-setting-privacy-blocked").then((module) => module.default));
const PopupSettingPrivacySessions = asyncComponentLoader(() => import("../containers/popup/popup-setting-privacy-sessions").then((module) => module.default));
const PopupSettingPrivacyVerification = asyncComponentLoader(() => import("../containers/popup/popup-setting-privacy-verification").then((module) => module.default));
const PopupSettingPrivacyVerificationSet = asyncComponentLoader(() => import("../containers/popup/popup-setting-privacy-verification-set").then((module) => module.default));
const PopupSettingProfile = asyncComponentLoader(() => import("../containers/popup/popup-setting-profile").then((module) => module.default));
const PopupSettingProfileUsername = asyncComponentLoader(() => import("../containers/popup/popup-setting-profile-username").then((module) => module.default));
const PopupSettingProfilePhone = asyncComponentLoader(() => import("../containers/popup/popup-setting-profile-phone").then((module) => module.default));
const PopupSettingProfilePhoneVerify = asyncComponentLoader(() => import("../containers/popup/popup-setting-profile-phone-verify").then((module) => module.default));
const PopupSettingStorage = asyncComponentLoader(() => import("../containers/popup/popup-setting-storage").then((module) => module.default));
const PopupStickerAdd = asyncComponentLoader(() => import("../containers/popup/popup-sticker-add").then((module) => module.default));
const PopupStickerGroup = asyncComponentLoader(() => import("../containers/popup/popup-sticker-group").then((module) => module.default));
const PopupEditContact = asyncComponentLoader(() => import("../containers/popup/popup-edit-contact").then((module) => module.default));
const PopupSharedMedia = asyncComponentLoader(() => import("../containers/popup/popup-sharedmedia").then((module) => module.default));
const PopupCamera = asyncComponentLoader(() => import("../containers/popup/popup-camera").then((module) => module.default));
const PopupUpdate = asyncComponentLoader(() => import("../containers/popup/popup-update").then((module) => module.default));
const PopupUsername = asyncComponentLoader(() => import("../containers/popup/popup-username").then((module) => module.default));
const PopupConvertToGroup = asyncComponentLoader(() => import("../containers/popup/popup-convert-to-group").then((module) => module.default));
const Roadmap = asyncComponentLoader(() => import("../containers/roadmap").then((module) => module.default));
const PopupBuyCharge = asyncComponentLoader(() => import("../components/popup/buy-charge").then((module) => module.default));
const PopupContacts = asyncComponentLoader(() => import("../components/popup/contact/contacts").then((module) => module.default));
const PopupReceiptContainer = asyncComponentLoader(() => import("../containers/popup/popup-reciept").then((module) => module.default));
const PopupProfileIvandHistoryComponent = asyncComponentLoader(() => import("../components/popup/profile/ivand/history").then((module) => module.default));
const PopupElectricBillComponent = asyncComponentLoader(() => import("../components/popup/electric-bill").then((module) => module.default));
const PopupMyBills = asyncComponentLoader(() => import("../containers/popup/popup_my_bills").then((module) => module.default));
const PopupMyBillsEditComponent = asyncComponentLoader(() => import("../components/popup/electric-bill/edit-bills").then((module) => module.default));
const PopupMyBillsAddComponent = asyncComponentLoader(() => import("../components/popup/electric-bill/add-bills").then((module) => module.default));
const PopupBillInquiryComponent = asyncComponentLoader(() => import("../components/popup/electric-bill/bill-inquiry").then((module) => module.default));
const PopupBranchInfoComponent = asyncComponentLoader(() => import("../components/popup/electric-bill/branch-info").then((module) => module.default));
const PopupMainGiftSticker = asyncComponentLoader(() => import("../components/popup/sticker/gift").then((module) => module.default));
const PopupGiftStickerListContainer = asyncComponentLoader(() => import("../containers/popup/popup-gift-sticker-list").then((module) => module.default));
const PopupMyVirtualGiftList = asyncComponentLoader(() => import("../components/popup/sticker/gift/my-cards-list").then((module) => module.default));
const PopupGiftCardDetail = asyncComponentLoader(() => import("../containers/popup/popup-giftcard-detail").then((module) => module.default));
const PopupBuyData = asyncComponentLoader(() => import("../components/popup/internet-packages").then((module) => module.default));
const PopupBuyInternetPackageList = asyncComponentLoader(() => import("../containers/popup/popup-buy-internet-list").then((module) => module.default));
const PopupIgashtProvinceComponent = asyncComponentLoader(() => import("../components/popup/igasht/province").then((module) => module.default));
const PopupIgashtLocationSearchComponent = asyncComponentLoader(() => import("../components/popup/igasht/location-search").then((module) => module.default));
const PopupActivatedGiftList = asyncComponentLoader(() => import("../components/popup/sticker/gift/activated-cards").then((module) => module.default));
const PopupFinancialHistory = asyncComponentLoader(() => import("../components/popup/financial").then((module) => module.default));
const PopupFinancialInfo = asyncComponentLoader(() => import("../components/popup/financial/info").then((module) => module.default));
const PopupIgashtBuyTicket = asyncComponentLoader(() => import("../components/popup/igasht/buy-ticket").then((module) => module.default));
const PopupIgashtTicketListComponent = asyncComponentLoader(() => import("../components/popup/igasht/ticket-list").then((module) => module.default));
const PopupIgashtQrCodeComponent = asyncComponentLoader(() => import("../components/popup/igasht/ticket-qr-code").then((module) => module.default));
const PopupShowMessage = asyncComponentLoader(() => import("../components/popup/show-message").then((module) => module.default));
const PopupInquiryMci = asyncComponentLoader(() => import("../components/popup/inquiry/inquiry-mci").then((module) => module.default));
const PopupInquiryTelecom = asyncComponentLoader(() => import("../components/popup/inquiry/inquiry-telecom").then((module) => module.default));
const PopupMapComponent = asyncComponentLoader(() => import("../components/popup/Map").then((module) => module.default));
const PopupNearBy = asyncComponentLoader(() => import("../containers/popup/popup-near-by").then((module) => module.default));
const PopupNewsAddComment = asyncComponentLoader(() => import("../components/popup/news-comment").then((module) => module.default));
const PopupPecWalletComponent = asyncComponentLoader(() => import("../components/popup/wallet/pec").then((module) => module.default));
const PopupPecWalletPayComponent = asyncComponentLoader(() => import("../components/popup/wallet/pec/amount").then((module) => module.default));
const PopupStoryGallery = asyncComponentLoader(() => import("../containers/popup/popup-story-gallery").then((module) => module.default));
const PopupStoryViews = asyncComponentLoader(() => import("../containers/popup/popup-story-views").then((module) => module.default));
const PopupCartInfo = asyncComponentLoader(() => import("../containers/popup/popup-gift-card-info").then((module) => module.default));
const PopuoAdd2HS = asyncComponentLoader(() => import("../containers/popup/popup-add-to-hs").then((module) => module.default));

const popupsMap: { [route: string]: JSXElementConstructor<FC | any> } = {
  [POPUP_MAP]: PopupMapComponent,
  [POPUP_NEAR_BY]: PopupNearBy,
  [POPUP_ATTACHMENT_CONTACT]: PopupAttachmentContact,
  [POPUP_IGASHT_LOCATION_SEARCH]: PopupIgashtLocationSearchComponent,
  [POPUP_EDIT_MY_BILLS]: PopupMyBillsEditComponent,
  [POPUP_BILL_INQUIRY]: PopupBillInquiryComponent,
  [POPUP_ADD_MY_BILLS]: PopupMyBillsAddComponent,
  [POPUP_BRANCH_INFO]: PopupBranchInfoComponent,
  [POPUP_IGASHT_BUY_TICKET]: PopupIgashtBuyTicket,
  [POPUP_IGASHT_TICKET_LIST]: PopupIgashtTicketListComponent,
  [POPUP_IGASHT_TICKET_QRCODE]: PopupIgashtQrCodeComponent,
  [POPUP_BUY_CHARGE]: PopupBuyCharge,
  [POPUP_MY_BILLS]: PopupMyBills,
  [POPUP_ELECTRIC_BILL]: PopupElectricBillComponent,
  [POPUP_EDIT_CONTACT]: PopupEditContact,
  [POPUP_IGASHT_TICKET]: PopupIgashtProvinceComponent,
  [POPUP_CAMERA]: PopupCamera,
  [POPUP_CONTACT_IMPORT]: PopupContactImport,
  [POPUP_CREATE_CONVERSATION]: PopupCreateConversation,
  [POPUP_CREATE_GROUP_CHANNEL]: PopupRoomCreateGroupChannel,
  [AVATARS_POPUP_GALLERY]: PopupAvatarsGallery,
  [MESSAGES_POPUP_GALLERY]: PopupMessagesGallery,
  [POPUP_PROFILE_IVAND]: PopupProfileIvand,
  [POPUP_ROOM]: PopupRoom,
  [POPUP_ROOM_ADD_MEMBER]: PopupRoomAddMember,
  [POPUP_ROOM_ADMIN_PERMISSION]: PopupRoomAdminPermission,
  [POPUP_ROOM_CHANNEL_EDIT]: PopupRoomChannelEdit,
  [POPUP_ROOM_CHANNEL_TYPE]: PopupRoomChannelType,
  [POPUP_ROOM_CHANNEL_TYPE]: PopupRoomChannelType,
  [POPUP_ROOM_FORWARD]: PopupRoomForward,
  [POPUP_ROOM_MEMBER_LIST]: PopupRoomMemberList,
  [POPUP_ROOM_PERMISSION]: PopupRoomPermission,
  [POPUP_ROOM_REPORT]: PopupRoomReport,
  [POPUP_SETTING]: PopupSetting,
  [POPUP_SETTING_CHAT_SETTING]: PopupSettingChatSetting,
  [POPUP_SETTING_NOTIFICATION]: PopupSettingNotification,
  [POPUP_SETTING_PRIVACY]: PopupSettingPrivacy,
  [POPUP_SETTING_PRIVACY_BLOCKED]: PopupSettingPrivacyBlocked,
  [POPUP_SETTING_PRIVACY_SESSIONS]: PopupSettingPrivacySessions,
  [POPUP_SETTING_PRIVACY_VERIFICATION]: PopupSettingPrivacyVerification,
  [POPUP_SETTING_PRIVACY_VERIFICATION_SET]: PopupSettingPrivacyVerificationSet,
  [POPUP_SETTING_PROFILE]: PopupSettingProfile,
  [POPUP_SETTING_PROFILE_USERNAME]: PopupSettingProfileUsername,
  [POPUP_SETTING_PROFILE_PHONE]: PopupSettingProfilePhone,
  [POPUP_SETTING_PROFILE_PHONE_VERIFY]: PopupSettingProfilePhoneVerify,
  [POPUP_SETTING_STORAGE]: PopupSettingStorage,
  [POPUP_STICKER_ADD]: PopupStickerAdd,
  [POPUP_STICKER_GROUP]: PopupStickerGroup,
  [POPUP_SHARED_MEDIA]: PopupSharedMedia,
  [POPUP_UPDATE]: PopupUpdate,
  [POPUP_USERNAME]: PopupUsername,
  [POPUP_CONVERT_TO_GROUP]: PopupConvertToGroup,
  [POPUP_CONTACTS]: PopupContacts,
  [POPUP_RECEIPT]: PopupReceiptContainer,
  [POPUP_IVAND_HISTORY]: PopupProfileIvandHistoryComponent,
  [POPUP_MAIN_VIRTUAL_GIFT]: PopupMainGiftSticker,
  [POPUP_VIRTUAL_GIFT_LIST]: PopupGiftStickerListContainer,
  [POPUP_MY_GIFT_LIST]: PopupMyVirtualGiftList,
  [POPUP_ACTIVATED_GIFT_LIST]: PopupActivatedGiftList,
  [POPUP_GIFT_CARD_DETAIL]: PopupGiftCardDetail,
  [POPUP_BUY_INTERNET_PACKAGES]: PopupBuyData,
  [POPUP_BUY_INTERNET_PACKAGE_LIST]: PopupBuyInternetPackageList,
  [POPUP_FINANCIAL_HISTORY]: PopupFinancialHistory,
  [POPUP_FINANCIAL_INFO]: PopupFinancialInfo,
  [POPUP_SHOW_MESSAGE]: PopupShowMessage,
  [POPUP_INQUIRY_MCI]: PopupInquiryMci,
  [POPUP_INQUIRY_TELECOM]: PopupInquiryTelecom,
  [POPUP_ADD_NEW_COMMENT]: PopupNewsAddComment,
  [POPUP_WALLET_PEC]: PopupPecWalletComponent,
  [POPUP_WALLET_PEC_PAYMENT]: PopupPecWalletPayComponent,
  [POPUP_STORY_GALLERY]: PopupStoryGallery,
  [POPUP_STORY_VIEWS]: PopupStoryViews,
  [POPUP_DELETE_ACCOUNT]: PopupDeleteAccount,
  [POPUP_CARD_INFO]: PopupCartInfo,
  [POPUP_ADD_TO_HS]: PopuoAdd2HS,
  null: Fragment,
};
const PopupRoutes: FC = () => {
  const location = useLocation();
  const Component = popupsMap[`${getUrlParam(location.search, "popup")}`];
  return Component ? <Component /> : <Fragment />;
};

const MainRoutes: FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigationService.navigation = navigate;
    checkHashbang();
  }, [navigate]);

  return (
    <>
      <Suspense fallback={<></>}>
        <PopupRoutes />
        <Routes>
          <Route path="/" element={checkAuth() ? <Messenger /> : <Login />} />
          <Route path={ROUTE_LOGIN_CODE} element={checkAuth() ? <Messenger /> : <LoginCode />} />
          <Route path={ROUTE_LOGIN_NICKNAME} element={checkAuth() ? <Messenger /> : <LoginNickname isSafe />} />
          <Route path={ROUTE_LOGIN_PASSWORD} element={checkAuth() ? <Messenger /> : <LoginPassword />} />
          <Route path={ROUTE_LOGIN_RECOVERY_EMAIL} element={checkAuth() ? <Messenger /> : <LoginRecoveryEmail />} />
          <Route path={ROUTE_LOGIN_RECOVERY_QUESTION} element={checkAuth() ? <Messenger /> : <LoginRecoveryQuestion />} />
          <Route path={ROUTE_LOGIN} element={checkAuth() ? <Messenger /> : <Login />} />
          <Route
            path={ROUTE_MESSENGER}
            element={
              <PrivateRoute>
                <Messenger />
              </PrivateRoute>
            }
          />
          <Route path={ROUTE_TOO_MANY_SESSION} element={<ActiveTab />} />
          <Route path="*" element={<NotFoundComponent />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default MainRoutes;
